import React, {useEffect, useRef, useState} from 'react'
import {useClientSitePreferences} from 'hec/context/site-preferences'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

interface TurnstileProps {
    onSuccess?: (token: string) => void
    onError?: (error: string) => void
    onExpire?: () => VideoEncoderBitrateMode
    className?: string
}

export const TurnstileWidget: React.FC<TurnstileProps> = ({
    onSuccess,
    onError,
    onExpire,
    className = ''
}) => {
    const turnstileRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const [isVerified, setIsVerified] = useState(false)
    const config = getConfig()
    const prefs = useClientSitePreferences()
    const {cloudFlareTurnstileSiteKey, cloudFlareTurnstileEnabled: enabled} =
        prefs.CloudFlareConfigs
    const siteKey = cloudFlareTurnstileSiteKey
    // For local testing only: comment out line above and uncomment below
    // const siteKey = config.app?.cloudflare?.dummyTestTurnstileSiteKey

    useEffect(() => {
        // Check if Turnstile script is loaded
        const checkTurnstileLoaded = setInterval(() => {
            if (window.turnstile) {
                setIsLoaded(true)
                clearInterval(checkTurnstileLoaded)
            }
        }, 100)

        return () => clearInterval(checkTurnstileLoaded)
    }, [])

    useEffect(() => {
        if (!isLoaded || !turnstileRef.current || !siteKey?.length) return

        // Set initial state to disabled
        setIsVerified(false)
        // Render Turnstile widget
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
                console.log('Turnstile verification successful')
                setIsVerified(true)
                onSuccess?.(token)
            },
            'error-callback': (error: string) => {
                console.error('Turnstile error:', error)
                onError?.(error)
            },
            'expired-callback': () => {
                console.warn('Turnstile token expired')
                onExpire?.()
            }
        })

        // Cleanup on unmount
        return () => {
            if (widgetIdRef.current) {
                window.turnstile.remove(widgetIdRef.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, siteKey])

    if (!enabled || !siteKey) {
        if (!enabled) {
            console.warn('Turnstile is disabled in site preferences')
        } else {
            console.warn('Turnstile site key not configured')
        }
        return null
    }

    return (
        <div
            data-testid="TurnstileWidget"
            id="cf-turnstile-widget"
            ref={turnstileRef}
            className={`cf-turnstile relative grow flex flex-col space-y-0 text-foreground ${className}`}
        />
    )
}

// Type augmentation for window.turnstile
declare global {
    interface Window {
        turnstile: {
            render: (container: HTMLElement, options: any) => string
            remove: (widgetId: string) => void
            reset: (widgetId: string) => void
        }
    }
}
